/* AppraisalComponents/AquasealExtraClipsBar.jsx
 * Phase C · C4d Aquaseal extraction (2026-02c).
 *
 * Aquaseal "Extra Aluminium Clips" request bar. Sits under the Aquaseal
 * section header. Operator types a quantity → upserts a single
 * [AUTO:extra_clips] Aquaseal line bound to the accessory catalogue row.
 * Two-way bound to the existing auto-line so reload picks up the
 * previous value; commit-on-blur keeps every keystroke from thrashing
 * the line list. qty=0 deletes the auto-line on commit.
 *
 * Extracted verbatim from AppraisalComponentsTab.js:4682-4743.
 */
import React from 'react';

export function AquasealExtraClipsBar({ lines, rates, onCommit }) {
  const existing = (lines || []).find(l =>
    /^\[AUTO:extra_clips\]/i.test((l && l.notes) || ''));
  const existingQty = Number(existing?.quantity) || 0;
  const [draft, setDraft] = React.useState(String(existingQty || ''));
  React.useEffect(() => {
    setDraft(String(existingQty || ''));
  }, [existingQty]);

  // Resolve which catalogue row will be used — preview to the operator
  // so they see exactly what's being added without saving first.
  const rate = (rates || []).find(r => r.is_accessory === true)
            || (rates || []).find(r =>
                 /\bclip\b/i.test(r.type_name || '')
                 && !/dektite/i.test(r.type_name || ''))
            || (rates || []).find(r => /clip/i.test(r.type_name || ''));

  const commit = () => {
    const n = Math.max(0, Math.floor(Number(draft) || 0));
    setDraft(n > 0 ? String(n) : '');
    onCommit(n);
  };

  const totalBuy = (Number(rate?.buy_cost) || 0) * (Number(draft) || 0);

  return (
    <div
      data-testid="aquaseal-extra-clips-bar"
      className="rounded-md border border-slate-200 bg-white px-3 py-2 flex items-center gap-3 flex-wrap"
    >
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
        + Extra Aluminium Clips
      </span>
      <input
        type="number"
        min={0}
        step={1}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
        placeholder="qty"
        data-testid="aquaseal-extra-clips-qty"
        className="w-20 text-xs border border-slate-300 rounded px-2 py-1 font-mono-num text-center"
      />
      {rate ? (
        <span className="text-[10px] text-slate-500" data-testid="aquaseal-extra-clips-rate-preview">
          → <strong className="text-slate-700">{rate.type_name}</strong>
          {' · '}buy ${Number(rate.buy_cost || 0).toFixed(2)}/ea
          {(Number(draft) || 0) > 0 && (
            <> · subtotal <strong className="text-orange-700 font-mono-num">${totalBuy.toFixed(2)}</strong></>
          )}
          {rate.supplier && <> · {rate.supplier}{rate.supplier_code ? ` (${rate.supplier_code})` : ''}</>}
        </span>
      ) : (
        <span className="text-[10px] text-amber-700" data-testid="aquaseal-extra-clips-no-rate">
          ⚠ No clip rate in Aquaseal catalogue — add one in <em>/component-rates</em>.
        </span>
      )}
      {existing && (
        <span
          className="ml-auto text-[10px] font-mono-num text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-full px-2 py-0.5"
          title="Currently saved on this appraisal"
        >
          live × {existingQty}
        </span>
      )}
    </div>
  );
}
